import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Destination, FlowAiResult, FlowChange, FlowPieceKind, InboundRoute, RestoreResult } from '@arcnet/contracts';
import { ApiClientError } from '../../../lib/api/client.js';
import {
  useBuildFlowFromWords, useDiscardFlowDraft, useFlowDrafts, usePublishFlowDraft, useRouteHistory, useRouteVersion, useSaveFlowDraft,
} from '../../../lib/api/hooks-tms.js';
import { useLiveRouting } from './useLive.js';
import { Badge, ErrorBanner, SearchInput } from '../../../components/ui/Primitives.js';
import { Icon, type IconName } from '../../../components/ui/Icon.js';
import { confirmAction } from '../../../components/ui/Confirm.js';
import { DestinationExtras, DestinationPicker, type DestinationExtra } from '../../../components/telephony/DestinationPicker.js';
import { IvrEditor } from '../IvrsTab.js';
import { RingGroupEditor } from '../RingGroupsTab.js';
import { QueueEditor } from '../QueuesTab.js';
import { HoursCheckEditor, MessageEditor } from '../CallStepsTab.js';
import { RouteEditor, type NumberChoice } from '../InboundRoutesTab.js';
import {
  CONTINUE, DEST_TYPE, KIND_GROUP, KIND_LABEL, bodyOf, buildTree, changeKey, exitsOf, getSlot, liveAsOf, newBody, pieceOf,
  piecesOf, reachByRoute, setSlot, type Body, type FlowNode, type Live, type TermKind,
} from './model.js';
import { namesFrom, type Names } from './describe.js';
import { HistoryList, KIND_ICON, StepChanges, historySince } from './HistoryList.js';
import { RestoreDialog } from './RestoreDialog.js';
import { shortAt } from './history.js';
import { NODE_W, layout, type Placed } from './layout.js';
import { lastFlowKey, type PanelMode } from './panel.js';

/**
 * The Flows editor (docs/41 §4, Fable's design): an inbound route's call flow, left to right, from
 * "Call comes in" to wherever the call ends. Every step is a piece of the account's routing, edited
 * in a side panel by its own Classic editor; changes go into a draft and reach calls on Publish.
 */

const TERM_ICON: Record<TermKind, IconName> = {
  extension: 'extension', voicemail: 'mail', external: 'callOut', hangup: 'close', goto: 'redial', unset: 'warning', missing: 'warning',
};
const TERM_LABEL: Record<TermKind, string> = {
  extension: 'Extension', voicemail: 'Voicemail', external: 'Outside number', hangup: 'Call ends', goto: 'Goes to',
  unset: 'Not set', missing: 'Missing',
};
const ZOOMS = [0.5, 0.67, 0.8, 1, 1.25, 1.5];
const NEW_KINDS: FlowPieceKind[] = ['message', 'hours', 'ivr', 'ring_group', 'queue'];
const NEW_WHAT: Partial<Record<FlowPieceKind, string>> = {
  message: 'Plays a recording, then carries on', hours: 'Open or closed, each its own way', ivr: '“Press 1 for sales”',
  ring_group: 'Rings several people', queue: 'Holds callers for the next free agent',
};
const EMPTY: ReadonlyMap<string, FlowChange> = new Map();

interface Draft {
  id: string; routeId: string | null; changes: ReadonlyMap<string, FlowChange>; savedAt: string | null; saving: boolean;
  /** A draft a restore opened as: when the version it went back to was made (docs/41 §12). */
  restoredAt?: string | null;
}
type Selection =
  | { type: 'piece'; kind: FlowPieceKind; id: string; nodeKey: string }
  | { type: 'exit'; parent: { kind: FlowPieceKind; id: string }; slot: string; nodeKey: string }
  | null;
interface LibAt { parent: { kind: FlowPieceKind; id: string }; slot: string; x: number; y: number; from: string; to: string }

/** Rough text width at 11 px, to know whether an edge label takes one line or two. */
function textWidth(text: string): number {
  let w = 0;
  for (const ch of text) w += ch === ' ' ? 0.28 : /\d/.test(ch) ? 0.58 : ch === ch.toUpperCase() && /[A-Z]/.test(ch) ? 0.68 : '.,·:;'.includes(ch) ? 0.3 : 0.53;
  return w * 11;
}

const time = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

export function FlowEditor({ tenantId, writable, canPublish, newFlowRequest }: {
  tenantId: string; writable: boolean; canPublish: boolean; newFlowRequest: number;
}) {
  // ------------------------------------------------------------------ live pieces
  const { live, ready } = useLiveRouting(tenantId);
  const draftsQ = useFlowDrafts(tenantId);
  const saveDraft = useSaveFlowDraft(tenantId);
  const discard = useDiscardFlowDraft(tenantId);
  const publish = usePublishFlowDraft(tenantId);
  const build = useBuildFlowFromWords(tenantId);
  // A flow described in words (docs/41 §7): open while somebody uses it, with what to check after.
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiNotes, setAiNotes] = useState<string[] | null>(null);

  const loaded = ready && draftsQ.data !== undefined;
  const others = useMemo(() => reachByRoute(live), [live]);

  // ------------------------------------------------------------------ which flow, and its draft
  const [flowId, setFlowId] = useState<string | null>(() => {
    try { return sessionStorage.getItem(lastFlowKey(tenantId)); } catch { return null; }
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [sel, setSel] = useState<Selection>(null);
  const [lib, setLib] = useState<LibAt | null>(null);
  const [problems, setProblems] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<unknown>(null);
  const [panelVersion, setPanelVersion] = useState(0);
  // Version history (docs/41 §12): the panel open, and the version shown (null: the routing as it is).
  const [hist, setHist] = useState<{ open: boolean; at: string | null }>({ open: false, at: null });
  const [restoring, setRestoring] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<Draft | null>(null);

  /** Flows to pick from: every live route, then flows that exist only in a draft. */
  const flows = useMemo(() => {
    const liveFlows = live.routes.map((r) => ({ id: r.id, name: r.name, isNew: false }));
    const fresh = (draftsQ.data?.data ?? []).filter((d) => !d.route_id).flatMap((d) => {
      const route = d.changes.find((c) => c.kind === 'inbound_route');
      return route ? [{ id: route.id, name: `${d.name} (new)`, isNew: true }] : [];
    });
    return [...liveFlows, ...fresh];
  }, [live.routes, draftsQ.data]);

  // The flow shown: the one chosen, else the first.
  const current = flows.find((f) => f.id === flowId) ?? flows[0] ?? null;
  const routeId = current?.id ?? null;

  useEffect(() => {
    if (!current) return;
    try { sessionStorage.setItem(lastFlowKey(tenantId), current.id); } catch { /* storage may be off */ }
  }, [current, tenantId]);

  // Load the draft for the flow shown, when the flow changes (not on every refetch, or it would undo typing).
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!loaded || !routeId || loadedFor.current === routeId) return;
    loadedFor.current = routeId;
    const d = (draftsQ.data?.data ?? []).find((x) => x.route_id === routeId
      || (!x.route_id && x.changes.some((c) => c.kind === 'inbound_route' && c.id === routeId)));
    setDraft(d ? {
      id: d.id, routeId: d.route_id, savedAt: d.updated_at, saving: false, restoredAt: d.restored_from?.at ?? null,
      changes: new Map(d.changes.map((c) => [changeKey(c.kind, c.id), c])),
    } : null);
    setSel(null); setLib(null); setProblems(new Map()); setError(null);
    setHist((h) => ({ ...h, at: null }));
  }, [loaded, routeId, draftsQ.data]);

  const changes = draft?.changes ?? EMPTY;

  /** Sends the draft as it is now, after a pause in the editing (or at once, before publishing). */
  const flush = useCallback(async () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    const d = pending.current;
    if (!d) return;
    pending.current = null;
    try {
      const res = await saveDraft.mutateAsync({ id: d.id, route_id: d.routeId, changes: [...d.changes.values()] });
      setDraft((cur) => (cur && cur.id === d.id ? { ...cur, savedAt: res.updated_at, saving: pending.current !== null } : cur));
    } catch (e) {
      setError(e);
      setDraft((cur) => (cur ? { ...cur, saving: false } : cur));
    }
  }, [saveDraft]);

  const commit = useCallback((next: Map<string, FlowChange>) => {
    if (!routeId) return;
    const base: Draft = draft ?? {
      id: crypto.randomUUID(), routeId: live.routes.some((r) => r.id === routeId) ? routeId : null,
      changes: EMPTY, savedAt: null, saving: false,
    };
    const d: Draft = { ...base, changes: next, saving: true };
    setDraft(d);
    setProblems(new Map());
    pending.current = d;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void flush(); }, 700);
  }, [draft, routeId, live.routes, flush]);

  /** Puts a piece into the draft as given. */
  const put = useCallback((kind: FlowPieceKind, id: string, body: Body, into?: Map<string, FlowChange>) => {
    const next = into ?? new Map(changes);
    const key = changeKey(kind, id);
    const was = next.get(key);
    const isLive = was ? was.op === 'update' : pieceOf(kind, id, live, EMPTY) !== null;
    next.set(key, { kind, id, op: isLive ? 'update' : 'create', body, ...(was?.base ? { base: was.base } : {}) });
    return next;
  }, [changes, live]);

  // A new flow, asked for from the page head: a route of its own, in a draft of its own.
  const lastRequest = useRef(newFlowRequest);
  useEffect(() => {
    if (newFlowRequest === lastRequest.current) return;
    lastRequest.current = newFlowRequest;
    const id = crypto.randomUUID();
    const body = newBody('inbound_route', null, live, EMPTY);
    const d: Draft = {
      id: crypto.randomUUID(), routeId: null, savedAt: null, saving: true,
      changes: new Map([[changeKey('inbound_route', id), { kind: 'inbound_route', id, op: 'create', body }]]),
    };
    loadedFor.current = id;
    setFlowId(id);
    setDraft(d);
    setSel({ type: 'piece', kind: 'inbound_route', id, nodeKey: `r:${id}` });
    pending.current = d;
    void flush();
  }, [newFlowRequest, live, flush]);

  // ------------------------------------------------------------------ version history (docs/41 §12)
  const isLiveRoute = !!routeId && live.routes.some((r) => r.id === routeId);
  const historyQ = useRouteHistory(tenantId, isLiveRoute ? routeId : null, hist.open);
  const versionQ = useRouteVersion(tenantId, isLiveRoute ? routeId : null, hist.open ? hist.at : null);
  /** The version shown, read-only; null while the routing as it is now is shown. */
  const viewing = hist.open && hist.at && versionQ.data?.version.id === hist.at ? versionQ.data : null;
  /** The account's routing with this route's steps as they were in that version. */
  const liveAt: Live = useMemo(() => (viewing ? liveAsOf(live, viewing.pieces) : live), [viewing, live]);
  /** What the version did, marked on its steps as a draft's changes are. */
  const marks = useMemo(() => new Map<string, FlowChange>((viewing?.version.steps ?? []).flatMap((st) => (st.after
    ? [[changeKey(st.kind, st.id), { kind: st.kind, id: st.id, op: st.before ? 'update' : 'create', body: st.after }] as const] : []))),
  [viewing]);
  const names: Names = useMemo(() => namesFrom(live, [
    ...(historyQ.data?.data ?? []).flatMap((v) => v.steps.map((st) => ({ kind: st.kind, id: st.id, body: st.after ?? st.before }))),
    ...(viewing?.pieces ?? []),
  ]), [live, historyQ.data, viewing]);

  // ------------------------------------------------------------------ what is drawn
  const tree = useMemo(() => {
    if (!routeId) return null;
    return viewing ? buildTree(routeId, liveAt, marks, new Map()) : buildTree(routeId, live, changes, others);
  }, [routeId, viewing, liveAt, marks, live, changes, others]);
  const problemKeys = problems;
  /** A flow with nowhere set for its calls yet: drawn as the first "+" rather than a "Not set yet". */
  const firstOnly = !!tree && tree.children.length === 1 && tree.children[0]!.node.term === 'unset';
  const drawn = useMemo(() => {
    if (!tree) return null;
    return layout(firstOnly ? { ...tree, children: [] } : tree);
  }, [tree, firstOnly]);

  // ------------------------------------------------------------------ the view: pan and zoom
  // A callback ref: the canvas is not there on the first render (the pieces are still loading), and
  // what measures it and listens to its wheel must start when it appears.
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const fitted = useRef<string | null>(null);

  useEffect(() => {
    const el = canvasEl;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [canvasEl]);

  const fit = useCallback(() => {
    // Measured now, not from the last resize: the panel may have just opened or closed.
    const w = canvasEl?.clientWidth ?? size.w, h = canvasEl?.clientHeight ?? size.h;
    if (!drawn || w === 0) return;
    const narrow = w < 600;
    const z = narrow ? 1 : Math.max(0.4, Math.min(1, (w - 32) / drawn.width, (h - 32) / drawn.height));
    const sw = drawn.width * z, sh = drawn.height * z;
    setView({ zoom: z, x: sw < w ? (w - sw) / 2 : 0, y: sh < h ? (h - sh) / 2 : 0 });
  }, [drawn, size, canvasEl]);

  // Opens at Fit, once per flow (owner, 6 October 2026: Fable's default).
  useEffect(() => {
    if (!drawn || size.w === 0 || !routeId || fitted.current === routeId) return;
    fitted.current = routeId;
    fit();
  }, [drawn, size.w, routeId, fit]);

  const zoomTo = useCallback((z: number, px = size.w / 2, py = size.h / 2) => {
    setView((v) => {
      const nz = Math.max(0.4, Math.min(1.5, z));
      return { zoom: nz, x: px - (px - v.x) * (nz / v.zoom), y: py - (py - v.y) * (nz / v.zoom) };
    });
  }, [size]);
  const step = (dir: 1 | -1) => {
    const z = view.zoom;
    const next = dir > 0 ? ZOOMS.find((s) => s > z + 0.001) ?? 1.5 : [...ZOOMS].reverse().find((s) => s < z - 0.001) ?? 0.5;
    zoomTo(next);
  };

  // No wheel handling (owner, 6 October 2026: "the canvas should only move when I click and drag on
  // empty space"). The wheel scrolls the page, the step library and the panel as anywhere else;
  // the zoom buttons and the arrow keys are the ways to move it without dragging (WCAG 2.5.7).

  const drag = useRef<{ x: number; y: number; vx: number; vy: number; id: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const t = e.target as Element;
    const empty = t === e.currentTarget || t.classList.contains('arc-flow-stage') || !!t.closest('svg.arc-flow-edges');
    if (!empty || e.button !== 0) return;
    drag.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y, id: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    setLib(null);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    setView((v) => ({ ...v, x: d.vx + e.clientX - d.x, y: d.vy + e.clientY - d.y }));
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id !== e.pointerId) return;
    drag.current = null;
    setDragging(false);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) {
      if (e.key === 'Escape') { setLib(null); }
      return;
    }
    const by = e.shiftKey ? 200 : 40;
    const pan = (dx: number, dy: number) => { e.preventDefault(); setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy })); };
    if (e.key === 'ArrowLeft') pan(by, 0);
    else if (e.key === 'ArrowRight') pan(-by, 0);
    else if (e.key === 'ArrowUp') pan(0, by);
    else if (e.key === 'ArrowDown') pan(0, -by);
    else if (e.key === '+' || e.key === '=') { e.preventDefault(); step(1); }
    else if (e.key === '-') { e.preventDefault(); step(-1); }
    else if (e.key === '0') { e.preventDefault(); fit(); }
    else if (e.key === 'Home') { e.preventDefault(); setView((v) => ({ ...v, x: 0, y: 0 })); }
    else if (e.key === 'Escape') setLib(null);
  };

  /** Pans the least distance that shows a node whole, with 24 px to spare. */
  const reveal = useCallback((key: string) => {
    const p = drawn?.byKey.get(key);
    if (!p || size.w === 0) return;
    setView((v) => {
      const left = v.x + p.x * v.zoom, right = v.x + (p.x + NODE_W) * v.zoom;
      const top = v.y + p.y * v.zoom, bottom = v.y + (p.y + p.h) * v.zoom;
      let { x, y } = v;
      if (left < 24) x += 24 - left; else if (right > size.w - 24) x -= right - (size.w - 24);
      if (top < 24) y += 24 - top; else if (bottom > size.h - 24) y -= bottom - (size.h - 24);
      return x === v.x && y === v.y ? v : { ...v, x, y };
    });
  }, [drawn, size]);
  const selKey = sel?.nodeKey ?? null;
  useEffect(() => { if (selKey) reveal(selKey); }, [selKey, size.w, reveal]);
  const shownVersion = viewing?.version.id ?? null;
  useEffect(() => {
    if (!shownVersion || !drawn) return;
    const first = drawn.placed.find((q) => q.node.badges.includes('new') || q.node.badges.includes('changed'));
    if (first) reveal(first.node.key);
  }, [shownVersion, drawn, reveal]);

  // ------------------------------------------------------------------ editing
  /** Sends a step's exit somewhere else. */
  const point = useCallback((parent: { kind: FlowPieceKind; id: string }, slot: string, d: Destination | null, into?: Map<string, FlowChange>) => {
    const body = bodyOf(parent.kind, parent.id, live, into ?? changes);
    if (!body) return into ?? new Map(changes);
    return put(parent.kind, parent.id, setSlot(body, slot, d), into);
  }, [live, changes, put]);

  const addStep = (at: LibAt, kind: FlowPieceKind) => {
    const parentBody = bodyOf(at.parent.kind, at.parent.id, live, changes);
    if (!parentBody) return;
    const id = crypto.randomUUID();
    const then = getSlot(parentBody, at.slot);
    let next = put(kind, id, newBody(kind, then, live, changes));
    next = point(at.parent, at.slot, { type: DEST_TYPE[kind]!, id } as Destination, next);
    commit(next);
    setLib(null);
    setSel({ type: 'piece', kind, id, nodeKey: `${nodeKeyOf(at)}` });
  };
  const nodeKeyOf = (at: LibAt) => {
    const parentNode = [...(drawn?.byKey.values() ?? [])].find((p) => p.node.piece?.id === at.parent.id);
    return `${parentNode?.node.key ?? ''}/${at.slot}`;
  };
  const pickExisting = (at: LibAt, kind: FlowPieceKind, id: string) => {
    commit(point(at.parent, at.slot, { type: DEST_TYPE[kind]!, id } as Destination));
    setLib(null);
  };
  const endHere = (at: LibAt, how: 'extension' | 'voicemail' | 'hangup') => {
    setLib(null);
    if (how === 'hangup') { commit(point(at.parent, at.slot, { type: 'hangup' })); return; }
    setSel({ type: 'exit', parent: at.parent, slot: at.slot, nodeKey: nodeKeyOf(at) });
  };

  /**
   * A described flow into the draft: its new steps, and the route sending its calls to the first of
   * them. The route's own hours make way for the description's (an hours check, if it has one).
   */
  const applyAi = (res: FlowAiResult) => {
    if (!routeId) return;
    let next = new Map(changes);
    for (const c of res.changes) next.set(changeKey(c.kind, c.id), c);
    const route = bodyOf('inbound_route', routeId, live, next);
    if (!route) return;
    next = put('inbound_route', routeId, { ...route, destination: res.start, hours: { mode: 'always' }, closed_destination: null }, next);
    commit(next);
    setSel(null);
    setLib(null);
    fitted.current = null;
  };
  const runAi = () => {
    setAiNotes(null);
    build.mutate({ prompt: aiText.trim(), ...(current ? { route_name: current.name.replace(/ \(new\)$/, '') } : {}) }, {
      onSuccess: (res) => { applyAi(res); setAiNotes(res.notes); },
    });
  };

  const selectedNode = useMemo(() => (selKey ? drawn?.byKey.get(selKey)?.node ?? null : null), [selKey, drawn]);
  const removeStep = () => {
    if (sel?.type !== 'piece' || !selectedNode?.parent?.node.piece) return;
    const body = bodyOf(sel.kind, sel.id, live, changes);
    const onward = body ? getSlot(body, CONTINUE[sel.kind]) : null;
    commit(point(selectedNode.parent.node.piece, selectedNode.parent.slot, onward));
    setSel(null);
  };

  const closePanel = async () => {
    setSel(null);
  };

  // ------------------------------------------------------------------ publish and discard
  const doPublish = async () => {
    if (!draft) return;
    setError(null);
    await flush();
    try {
      const res = await publish.mutateAsync(draft.id);
      setDraft(null);
      setProblems(new Map());
      setSel(null);
      loadedFor.current = res.route_id;
      setFlowId(res.route_id);
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 422 && e.problem?.errors?.length) {
        const map = new Map(e.problem.errors.map((x) => [x.field, x.message]));
        setProblems(map);
        const first = e.problem.errors[0]!.field;
        const [kind, id] = first.split(':') as [FlowPieceKind, string];
        const p = [...(drawn?.byKey.values() ?? [])].find((q) => q.node.piece?.id === id);
        if (p) setSel({ type: 'piece', kind, id, nodeKey: p.node.key });
      } else {
        setError(e);
      }
    }
  };
  const doDiscard = async () => {
    if (!draft) return;
    const ok = await confirmAction({
      title: 'Discard this draft?', body: 'Its changes have not reached any call, and they will be gone.',
      action: 'Discard draft', danger: true,
    });
    if (!ok) return;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    pending.current = null;
    if (draft.savedAt || saveDraft.isPending) await discard.mutateAsync(draft.id).catch(() => undefined);
    const wasNew = !draft.routeId;
    setDraft(null); setSel(null); setProblems(new Map());
    if (wasNew) { loadedFor.current = null; setFlowId(live.routes[0]?.id ?? null); }
  };

  const onRestored = (r: RestoreResult) => {
    setRestoring(false);
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    pending.current = null;
    setSel(null);
    if (r.mode === 'flows') {
      // The draft the restore opened as is loaded when the drafts come back.
      setDraft(null);
      loadedFor.current = null;
      setHist({ open: false, at: null });
    } else {
      setHist({ open: true, at: null });
    }
  };
  const openRestore = async () => { await flush(); setRestoring(true); };

  // ------------------------------------------------------------------ the bar's status
  const firstProblem = problems.size > 0 ? [...problems.entries()][0]! : null;
  const problemStep = firstProblem
    ? [...(drawn?.byKey.values() ?? [])].find((p) => p.node.piece && `${p.node.piece.kind}:${p.node.piece.id}` === firstProblem[0])
    : undefined;
  const status: ReactNode = firstProblem ? (
    <span className="arc-flow-status" data-tone="warn">
      <span className="arc-dot" />
      <span>
        Not published ·{' '}
        {problemStep ? (
          <button type="button" className="arc-flow-status-step"
            onClick={() => { const pc = problemStep.node.piece!; setSel({ type: 'piece', kind: pc.kind, id: pc.id, nodeKey: problemStep.node.key }); }}>
            {problemStep.node.name}
          </button>
        ) : null}
        {problemStep && firstProblem[1].startsWith(problemStep.node.name)
          ? firstProblem[1].slice(problemStep.node.name.length).replace(/\.$/, '')
          : ' needs attention'}
        {problems.size > 1 ? ` · ${problems.size - 1} more ${problems.size === 2 ? 'step needs' : 'steps need'} attention` : ''}
      </span>
    </span>
  ) : draft ? (
    <span className="arc-flow-status" data-tone="warn">
      <span className="arc-dot" />
      <span>
        Draft{draft.restoredAt ? ` · restored from ${shortAt(draft.restoredAt)}` : ''} · {draft.changes.size} {draft.changes.size === 1 ? 'change' : 'changes'}
        {draft.saving ? ' · saving…' : draft.savedAt ? ` · saved ${time(draft.savedAt)}` : ''}
      </span>
    </span>
  ) : (
    <span className="arc-flow-status" data-tone="ok"><span className="arc-dot" /><span>Live on the switch</span></span>
  );

  // ------------------------------------------------------------------ render
  const extras: DestinationExtra[] = useMemo(() => [...changes.values()].flatMap((c) => {
    const type = DEST_TYPE[c.kind];
    return type ? [{ type, id: c.id, name: String(c.body.name ?? '') }] : [];
  }), [changes]);

  if (!loaded) return <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 320 }} /></div>;
  if (!current || !tree) {
    return (
      <div className="arc-card arc-card-body">
        <p className="arc-muted" style={{ margin: 0 }}>No call flows yet. Start one with New call flow.</p>
      </div>
    );
  }

  const z = view.zoom;
  const W = drawn?.width ?? 0, H = drawn?.height ?? 0;
  const showOverview = W * z > size.w + 1 || H * z > size.h + 1;
  const height = 'max(480px, calc(100vh - 300px))';

  return (
    <DestinationExtras.Provider value={extras}>
      {error !== null && <ErrorBanner error={error} />}
      <div className="arc-card arc-flow-editor">
        <div className="arc-flow-bar">
          <select className="arc-select" aria-label="Call flow" value={current.id}
            onChange={(e) => { void flush(); setFlowId(e.target.value); }}>
            {flows.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          {viewing ? (
            <span className="arc-flow-status" data-tone="info">
              <span className="arc-dot" /><span>Viewing {shortAt(viewing.version.at)} · read-only</span>
            </span>
          ) : status}
          <div className="arc-flow-zoom" role="group" aria-label="Zoom">
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Zoom out" title="Zoom out (−)" onClick={() => step(-1)}>−</button>
            <span className="arc-flow-zoom-level" aria-live="polite">{Math.round(z * 100)}%</span>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Zoom in" title="Zoom in (+)" onClick={() => step(1)}>+</button>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" title="Fit the whole flow (0)" onClick={fit}>Fit</button>
          </div>
          {writable && !viewing && (
            <button type="button" className="arc-btn arc-btn-sm" aria-expanded={aiOpen}
              onClick={() => { setAiOpen((o) => !o); setAiNotes(null); }}>
              <Icon name="message" size={13} />Describe it
            </button>
          )}
          {!viewing && (
            <button type="button" className="arc-btn arc-btn-sm" aria-expanded={hist.open} disabled={!isLiveRoute}
              title={isLiveRoute ? undefined : 'A flow has history once it is published'}
              onClick={() => { setSel(null); setHist((h) => ({ open: !h.open, at: null })); }}>
              <Icon name="history" size={13} />History
            </button>
          )}
          <div className="arc-spacer" />
          {viewing && (
            <>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => { setSel(null); setHist({ open: true, at: null }); }}>
                <Icon name="chevronLeft" size={13} />Back to current
              </button>
              {canPublish && (
                <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" onClick={() => { void openRestore(); }}>
                  <Icon name="restore" size={13} />Restore this version
                </button>
              )}
            </>
          )}
          {!viewing && writable && draft && (
            <button type="button" className="arc-btn arc-btn-sm arc-btn-ghost" onClick={() => { void doDiscard(); }} disabled={discard.isPending}>
              Discard draft
            </button>
          )}
          {!viewing && canPublish && (
            <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" disabled={!draft || publish.isPending}
              title={draft ? undefined : 'Nothing to publish: no changes yet'} onClick={() => { void doPublish(); }}>
              {publish.isPending ? 'Publishing…' : 'Publish'}
            </button>
          )}
        </div>

        {aiOpen && writable && !viewing && (
          <form className="arc-flow-ai" onSubmit={(e) => { e.preventDefault(); runAi(); }}>
            <label className="arc-label" htmlFor="flow-ai">Describe what should happen to a call</label>
            <textarea id="flow-ai" className="arc-input" maxLength={1000} value={aiText} autoFocus
              onChange={(e) => setAiText(e.target.value)}
              placeholder="Weekdays 8 to 5, ring Sales for 20 seconds, then the Support queue. After hours, play the after-hours message, then Reception's voicemail." />
            <div className="arc-flow-ai-actions">
              <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={build.isPending || aiText.trim().length < 3}>
                {build.isPending ? 'Building…' : 'Build draft'}
              </button>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => { setAiOpen(false); setAiNotes(null); }}>Close</button>
              <span className="arc-subtle" style={{ fontSize: 12 }}>
                Uses this account’s own menus, lists, queues and recordings, and replaces where this flow’s calls go first.
                It is a draft: nothing reaches a call until you publish.
              </span>
            </div>
            {build.error !== null && <p className="arc-error">{(build.error as Error).message}</p>}
            {aiNotes && (aiNotes.length > 0 ? (
              <>
                <p className="arc-muted" style={{ margin: '10px 0 0', fontSize: 12.5 }}>Draft built. Check these before publishing:</p>
                <ul className="arc-flow-ai-notes">{aiNotes.map((n) => <li key={n}>{n}</li>)}</ul>
              </>
            ) : <p className="arc-muted" style={{ margin: '10px 0 0', fontSize: 12.5 }}>Draft built. Look it over, then publish.</p>)}
          </form>
        )}

        <div className="arc-flow-split" style={{ height }}>
          <div ref={setCanvasEl} className="arc-flow-canvas" tabIndex={0} role="application"
            aria-label="Call flow. Drag empty space or use the arrow keys to move; plus and minus zoom."
            data-dragging={dragging || undefined} data-readonly={viewing ? true : undefined}
            style={{ backgroundPosition: `${view.x}px ${view.y}px` }}
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}
            onKeyDown={onKeyDown}>
            {drawn && (
              <div className="arc-flow-stage" style={{ width: W, height: H, transform: `translate(${view.x}px, ${view.y}px) scale(${z})` }}>
                <svg className="arc-flow-edges" width={W} height={H}>
                  {drawn.edges.map((e) => (
                    <g key={`${e.from.node.key}>${e.to.node.key}`}>
                      <path className="arc-flow-edge" d={e.path} />
                      <path className="arc-flow-edge-head" d={e.head} />
                    </g>
                  ))}
                </svg>
                {drawn.edges.filter((e) => e.label).map((e) => {
                  const lines = textWidth(e.label!) <= 72 ? 1 : 2;
                  const lh = lines * 14 + 2;
                  const digit = e.slot.startsWith('option:') ? e.slot.slice(7) : null;
                  return (
                    <span key={`l:${e.to.node.key}`} className="arc-flow-edge-label" title={e.label!}
                      style={{ left: e.labelX, top: e.labelY - lh / 2 }}>
                      {digit ? <><b>{digit}</b>{e.label!.slice(digit.length)}</> : e.label}
                    </span>
                  );
                })}
                {drawn.placed.map((p) => (
                  <NodeCard key={p.node.key} p={p} selected={selKey === p.node.key}
                    problem={!viewing && !!p.node.piece && problemKeys.has(`${p.node.piece.kind}:${p.node.piece.id}`)}
                    changed={viewing ? (p.node.badges.includes('new') ? 'new' : p.node.badges.includes('changed') ? 'changed' : undefined) : undefined}
                    onSelect={() => {
                      const n = p.node;
                      if (n.piece) { setSel({ type: 'piece', kind: n.piece.kind, id: n.piece.id, nodeKey: n.key }); return; }
                      if (n.goto) {
                        const target = [...drawn.byKey.values()].find((q) => q.node.piece?.id === n.goto!.id);
                        if (target) setSel({ type: 'piece', kind: n.goto.kind, id: n.goto.id, nodeKey: target.node.key });
                        return;
                      }
                      if (n.parent?.node.piece && writable && !viewing) setSel({ type: 'exit', parent: n.parent.node.piece, slot: n.parent.slot, nodeKey: n.key });
                    }} />
                ))}
                {writable && !viewing && drawn.edges.map((e) => {
                  const parent = e.from.node.piece;
                  if (!parent) return null;
                  const open = lib?.parent.id === parent.id && lib.slot === e.slot;
                  return (
                    <button key={`p:${e.to.node.key}`} type="button" className="arc-flow-plus" aria-expanded={open}
                      aria-label={`Add a step between ${e.from.node.name} and ${e.to.node.name}`}
                      style={{ left: e.plusX, top: e.plusY, transform: `scale(${1 / z})` }}
                      onClick={() => setLib(open ? null : {
                        parent, slot: e.slot, from: e.from.node.name, to: e.to.node.name,
                        x: view.x + e.plusX * z, y: view.y + e.plusY * z,
                      })}>
                      <Icon name="plus" size={11} />
                    </button>
                  );
                })}
                {firstOnly && writable && !viewing && (() => {
                  const root = drawn.placed[0]!;
                  const x1 = root.x + NODE_W, y1 = root.y + root.h / 2;
                  const open = lib?.parent.id === routeId && lib.slot === 'destination';
                  return (
                    <>
                      <svg className="arc-flow-edges" width={W + 200} height={H}><path className="arc-flow-edge" d={`M${x1},${y1} H${x1 + 34}`} /></svg>
                      <span className="arc-flow-first" style={{ left: x1 + 34, top: y1 - 10 }}>
                        <button type="button" className="arc-flow-plus" aria-label="Add the first step" aria-expanded={open}
                          onClick={() => setLib(open ? null : {
                            parent: { kind: 'inbound_route', id: routeId! }, slot: 'destination', from: root.node.name, to: 'the end',
                            x: view.x + (x1 + 44) * z, y: view.y + y1 * z,
                          })}>
                          <Icon name="plus" size={11} />
                        </button>
                        Add the first step, or
                        <button type="button" className="arc-flow-first-or" onClick={() => { setAiOpen(true); setAiNotes(null); }}>describe it</button>
                      </span>
                    </>
                  );
                })()}
              </div>
            )}

            {showOverview && drawn && (
              <Overview W={W} H={H} placed={drawn.placed} view={view} size={size}
                onMove={(cx, cy) => setView((v) => ({ ...v, x: size.w / 2 - cx * v.zoom, y: size.h / 2 - cy * v.zoom }))} />
            )}

            {lib && (
              <Library at={lib} size={size} live={live} changes={changes}
                onNew={(k) => addStep(lib, k)} onUse={(k, id) => pickExisting(lib, k, id)} onEnd={(how) => endHere(lib, how)}
                onClose={() => setLib(null)} />
            )}
          </div>

          {sel && viewing && sel.type === 'piece' && (
            <Panel key={`${selKey}:${viewing.version.id}`} tenantId={tenantId} sel={sel} live={liveAt} changes={EMPTY}
              node={selectedNode} problem={null} writable={false} closeLabel="Back to history"
              note={(() => {
                const touched = viewing.version.steps.filter((st) => st.kind === sel.kind && st.id === sel.id);
                return (
                  <>
                    <p className="arc-flow-panel-note">As it was on {shortAt(viewing.version.at)}. Read-only.</p>
                    {touched.length > 0 && (
                      <div className="arc-history-detail" style={{ border: 0, padding: '0 0 12px' }}>
                        <StepChanges steps={touched} names={names} />
                      </div>
                    )}
                  </>
                );
              })()}
              onApplyPiece={() => undefined} onApplyExit={() => undefined} onRemove={null}
              onClose={() => setSel(null)} />
          )}
          {!sel && hist.open && routeId && (
            <aside className="arc-flow-panel arc-history-panel" aria-label="History">
              <div className="arc-flow-panel-head">
                <span className="arc-flow-node-icon"><Icon name="history" size={14} /></span>
                <div className="arc-stack" style={{ flex: 1, minWidth: 0 }}>
                  <span className="arc-flow-panel-kind">History</span>
                  <span className="arc-flow-panel-name">
                    {current.name}{historyQ.data ? ` · ${historySince(historyQ.data.data)}` : ''}
                  </span>
                </div>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label="Close history"
                  onClick={() => setHist({ open: false, at: null })}><Icon name="close" size={14} /></button>
              </div>
              {historyQ.isLoading ? <div className="arc-flow-panel-body"><div className="arc-skeleton" style={{ height: 160 }} /></div>
                : historyQ.error ? <div className="arc-flow-panel-body"><ErrorBanner error={historyQ.error} /></div>
                  : (
                    <HistoryList tenantId={tenantId} versions={historyQ.data?.data ?? []} names={names} viewing={hist.at}
                      onView={(at) => { setSel(null); setHist({ open: true, at }); }}
                      draft={draft ? { changes: draft.changes.size, savedAt: draft.savedAt } : null}
                      canName={writable}
                      onStep={(_kind, id) => {
                        const q = [...(drawn?.byKey.values() ?? [])].find((x) => x.node.piece?.id === id);
                        if (q) reveal(q.node.key);
                      }}
                      onEscape={() => setHist({ open: false, at: null })} />
                  )}
            </aside>
          )}
          {sel && !viewing && (
            <Panel key={`${selKey}:${panelVersion}`} tenantId={tenantId} sel={sel} live={live} changes={changes}
              node={selectedNode} problem={sel.type === 'piece' ? problems.get(`${sel.kind}:${sel.id}`) ?? null : null}
              writable={writable}
              onApplyPiece={(kind, id, body) => { commit(put(kind, id, body)); setPanelVersion((v) => v + 1); }}
              onApplyExit={(parent, slot, d) => { commit(point(parent, slot, d)); setSel(null); }}
              onRemove={selectedNode?.parent?.node.piece && sel.type === 'piece' ? removeStep : null}
              onClose={() => { void closePanel(); }} />
          )}
        </div>
      </div>
      {restoring && viewing && routeId && (
        <RestoreDialog tenantId={tenantId} routeId={routeId} routeName={current.name} versionId={viewing.version.id}
          at={viewing.version.at} plan={viewing.plan} names={names}
          onClose={() => setRestoring(false)} onRestored={onRestored} />
      )}
    </DestinationExtras.Provider>
  );
}

// ------------------------------------------------------------------ a node or an end
function NodeCard({ p, selected, problem, changed, onSelect }: {
  p: Placed; selected: boolean; problem: boolean;
  /** While a version is shown: what it did to this step. */
  changed?: 'new' | 'changed' | undefined;
  onSelect: () => void;
}) {
  const n = p.node;
  if (n.term) {
    return (
      <button type="button" className="arc-flow-term" data-goto={n.term === 'goto' || undefined}
        data-unset={n.term === 'unset' || n.term === 'missing' || undefined} data-selected={selected}
        style={{ left: p.x, top: p.y, height: p.h }} onClick={onSelect}
        aria-label={n.term === 'goto' ? `Goes to ${n.name}` : `${TERM_LABEL[n.term]}: ${n.name}`}>
        <span className="arc-flow-term-kind"><Icon name={TERM_ICON[n.term]} size={13} />{TERM_LABEL[n.term]}</span>
        <span className="arc-flow-term-value">
          {n.term === 'unset' ? <><span className="arc-dot" />Not set yet</> : n.name}
        </span>
      </button>
    );
  }
  const kind = n.piece!.kind;
  return (
    <button type="button" className="arc-flow-node" data-group={KIND_GROUP[kind]} data-selected={selected}
      data-problem={problem} data-changed={changed} style={{ left: p.x, top: p.y, height: p.h }} onClick={onSelect}>
      <span className="arc-flow-node-head">
        <span className="arc-flow-node-icon"><Icon name={KIND_ICON[kind]} size={13} /></span>
        <span className="arc-flow-node-kind">{KIND_LABEL[kind]}</span>
      </span>
      <span className="arc-flow-node-name">{n.name}</span>
      {n.detail.map((d, i) => <span key={i} className={`arc-flow-node-detail${n.mono ? ' arc-mono' : ''}`}>{d}</span>)}
      {n.now !== undefined && n.now !== null && (
        <span className="arc-flow-node-detail">
          <span className="arc-flow-now" data-tone={n.now ? 'ok' : undefined}><span className="arc-dot" />{n.now ? 'Open now' : 'Closed now'}</span>
        </span>
      )}
      {n.shared && <span className="arc-flow-node-shared"><Icon name="users" size={11} />Also in {n.shared}</span>}
      {n.badges.length > 0 && (
        <span className="arc-flow-node-badges">
          {n.badges.map((b) => b === 'new' ? <Badge key={b} tone="info"><span className="arc-dot" />new</Badge>
            : b === 'changed' ? <Badge key={b} tone="info"><span className="arc-dot" />changed</Badge>
              : b === 'needs-recording' ? <Badge key={b} tone="warn"><Icon name="warning" size={10} />needs a recording</Badge>
                : <Badge key={b} tone="warn"><Icon name="warning" size={10} />needs people</Badge>)}
        </span>
      )}
    </button>
  );
}

// ------------------------------------------------------------------ where you are
function Overview({ W, H, placed, view, size, onMove }: {
  W: number; H: number; placed: Placed[]; view: { zoom: number; x: number; y: number }; size: { w: number; h: number };
  onMove: (cx: number, cy: number) => void;
}) {
  const s = 150 / W;
  const at = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    onMove((e.clientX - r.left) / s, (e.clientY - r.top) / s);
  };
  const vx = Math.max(0, -view.x / view.zoom * s), vy = Math.max(0, -view.y / view.zoom * s);
  return (
    <div className="arc-flow-overview" style={{ height: H * s }} role="img" aria-label="Where you are in the flow"
      onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); at(e); }}
      onPointerMove={(e) => { if (e.buttons) at(e); }}>
      {placed.map((p) => (
        <span key={p.node.key} className="arc-flow-overview-node"
          style={{ left: p.x * s, top: p.y * s, width: NODE_W * s, height: p.h * s }} />
      ))}
      <span className="arc-flow-overview-view" style={{
        left: vx, top: vy,
        width: Math.min(150 - vx, size.w / view.zoom * s), height: Math.min(H * s - vy, size.h / view.zoom * s),
      }} />
    </div>
  );
}

// ------------------------------------------------------------------ the step library
function Library({ at, size, live, changes, onNew, onUse, onEnd, onClose }: {
  at: LibAt; size: { w: number; h: number }; live: Live; changes: ReadonlyMap<string, FlowChange>;
  onNew: (k: FlowPieceKind) => void; onUse: (k: FlowPieceKind, id: string) => void;
  onEnd: (how: 'extension' | 'voicemail' | 'hangup') => void; onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const existing = NEW_KINDS.flatMap((k) => piecesOf(k, live, changes).map((x) => ({ ...x, kind: k })))
    .filter((x) => x.name.toLowerCase().includes(q.trim().toLowerCase()));
  const W = 320;
  // As tall as it needs, up to the canvas less a margin; beyond that it scrolls inside itself.
  const H = Math.min(560, Math.max(200, size.h - 16));
  const left = at.x + 18 + W > size.w ? Math.max(8, at.x - 18 - W) : at.x + 18;
  const top = Math.max(8, Math.min(at.y - 40, size.h - H - 8));
  return (
    <div className="arc-flow-lib" role="dialog" aria-label="Add a step" style={{ left, top, maxHeight: H, overflowY: 'auto' }}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
      <div className="arc-flow-lib-head">
        <strong>Add a step</strong>
        <span>Between {at.from} and {at.to}</span>
      </div>
      <div className="arc-flow-lib-group">New step</div>
      {NEW_KINDS.map((k, i) => (
        <button key={k} ref={i === 0 ? first : undefined} type="button" className="arc-flow-lib-item" data-group={KIND_GROUP[k]} onClick={() => onNew(k)}>
          <span className="arc-flow-node-icon"><Icon name={KIND_ICON[k]} size={14} /></span>
          <span><strong>{KIND_LABEL[k]}</strong><span>{NEW_WHAT[k]}</span></span>
        </button>
      ))}
      <div className="arc-flow-lib-group">Or end the call here</div>
      <div className="arc-flow-lib-ends">
        <button type="button" className="arc-chip arc-chip-toggle" onClick={() => onEnd('extension')}><Icon name="extension" size={12} />Extension</button>
        <button type="button" className="arc-chip arc-chip-toggle" onClick={() => onEnd('voicemail')}><Icon name="mail" size={12} />Voicemail</button>
        <button type="button" className="arc-chip arc-chip-toggle" onClick={() => onEnd('hangup')}><Icon name="close" size={12} />Hang up</button>
      </div>
      <div className="arc-flow-lib-group">Or use one you have</div>
      <div className="arc-flow-lib-search">
        <SearchInput value={q} onChange={setQ} placeholder="Search menus, lists, queues, messages" label="Search steps" />
      </div>
      <div className="arc-flow-lib-foot">
        {existing.slice(0, 30).map((x) => (
          <button key={`${x.kind}:${x.id}`} type="button" className="arc-flow-lib-item" data-group={KIND_GROUP[x.kind]} onClick={() => onUse(x.kind, x.id)}>
            <span className="arc-flow-node-icon"><Icon name={KIND_ICON[x.kind]} size={14} /></span>
            <span><strong>{x.name}<span className="arc-flow-lib-sub">{KIND_LABEL[x.kind]}</span></strong></span>
          </button>
        ))}
        {existing.length === 0 && <p className="arc-subtle" style={{ fontSize: 12, padding: '0 14px', margin: 0 }}>Nothing matches.</p>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the side panel
function Panel({ tenantId, sel, live, changes, node, problem, writable, note, closeLabel = 'Cancel', onApplyPiece, onApplyExit, onRemove, onClose }: {
  tenantId: string; sel: NonNullable<Selection>; live: Live; changes: ReadonlyMap<string, FlowChange>;
  node: FlowNode | null; problem: string | null; writable: boolean;
  /** Above the settings: a version's step says when it is from and what changed. */
  note?: ReactNode; closeLabel?: string;
  onApplyPiece: (kind: FlowPieceKind, id: string, body: Body) => void;
  onApplyExit: (parent: { kind: FlowPieceKind; id: string }, slot: string, d: Destination | null) => void;
  onRemove: (() => void) | null; onClose: () => void;
}) {
  const formId = 'arc-flow-panel-form';
  const done = () => onClose();

  if (sel.type === 'exit') {
    const parentRead = pieceOf(sel.parent.kind, sel.parent.id, live, changes);
    const parentBody = bodyOf(sel.parent.kind, sel.parent.id, live, changes) ?? {};
    const label = exitsOf(sel.parent.kind, parentBody).find((x) => x.slot === sel.slot)?.label;
    return (
      <aside className="arc-flow-panel" aria-label="Where calls go">
        <div className="arc-flow-panel-head">
          <span className="arc-flow-node-icon"><Icon name="callOut" size={14} /></span>
          <div className="arc-stack" style={{ flex: 1, minWidth: 0 }}>
            <span className="arc-flow-panel-kind">Where calls go</span>
            <span className="arc-flow-panel-name arc-truncate">{parentRead?.name ?? ''}{label ? ` · ${label}` : ''}</span>
          </div>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label="Close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>
        <ExitForm key={`${sel.parent.id}:${sel.slot}`} formId={formId} tenantId={tenantId}
          value={getSlot(parentBody, sel.slot)} onApply={(d) => onApplyExit(sel.parent, sel.slot, d)} />
        <div className="arc-flow-panel-foot">
          {writable && <button type="submit" form={formId} className="arc-btn arc-btn-primary arc-btn-sm">Apply to draft</button>}
          <button type="button" className="arc-btn arc-btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </aside>
    );
  }

  const read = pieceOf(sel.kind, sel.id, live, changes);
  if (!read) return null;
  const panel: PanelMode = { formId, onApply: (body) => onApplyPiece(sel.kind, sel.id, body) };
  const shared = node?.shared;
  let editor: ReactNode;
  switch (sel.kind) {
    case 'inbound_route': {
      const numbers: NumberChoice[] = live.numbers.map((n) => {
        const r = live.routes.find((x) => x.numbers.some((m) => m.id === n.id));
        return { id: n.id, e164: n.e164, label: n.label, routeId: r?.id ?? null, routeName: r?.name ?? null };
      }).sort((a, b) => a.e164.localeCompare(b.e164));
      editor = <RouteEditor tenantId={tenantId} existing={read as InboundRoute} numbers={numbers} seedNumberId={null}
        writable={writable} onSaved={done} onDeleted={done} onCancel={done} panel={panel} />;
      break;
    }
    case 'ivr': editor = <IvrEditor tenantId={tenantId} existing={read as never} onDone={done} panel={panel} />; break;
    case 'ring_group': editor = <RingGroupEditor tenantId={tenantId} existing={read as never} onDone={done} panel={panel} />; break;
    case 'queue': editor = <QueueEditor tenantId={tenantId} existing={read as never} onDone={done} panel={panel} />; break;
    case 'message': editor = <MessageEditor tenantId={tenantId} existing={read as never} onDone={done} panel={panel} />; break;
    case 'hours': editor = <HoursCheckEditor tenantId={tenantId} existing={read as never} onDone={done} panel={panel} />; break;
  }

  return (
    <aside className="arc-flow-panel" aria-label={`${KIND_LABEL[sel.kind]} ${read.name}`}>
      <div className="arc-flow-panel-head">
        <span className="arc-flow-node-icon" data-group={KIND_GROUP[sel.kind]}><Icon name={KIND_ICON[sel.kind]} size={14} /></span>
        <div className="arc-stack" style={{ flex: 1, minWidth: 0 }}>
          <span className="arc-flow-panel-kind">{KIND_LABEL[sel.kind]}</span>
          <span className="arc-flow-panel-name arc-truncate">{read.name}</span>
        </div>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label="Close" onClick={onClose}><Icon name="close" size={14} /></button>
      </div>
      <div className="arc-flow-panel-body">
        {note}
        {problem && <p className="arc-error" style={{ marginTop: 0 }}>{problem}</p>}
        {shared && (
          <p className="arc-flow-panel-note">Also used by <strong>{shared}</strong>. A change here applies there too when you publish.</p>
        )}
        <fieldset disabled={!writable} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>{editor}</fieldset>
      </div>
      <div className="arc-flow-panel-foot">
        {writable && <button type="submit" form={formId} className="arc-btn arc-btn-primary arc-btn-sm">Apply to draft</button>}
        <button type="button" className="arc-btn arc-btn-sm" onClick={onClose}>{closeLabel}</button>
        <span className="arc-spacer" />
        {writable && onRemove && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onRemove}
            title="Takes this step out of this flow; the step before it then leads where this one led. The step itself stays.">
            Remove from flow
          </button>
        )}
      </div>
    </aside>
  );
}

/** Where one exit sends the call: an extension, a mailbox, a step, an outside number, or the end. */
function ExitForm({ formId, tenantId, value, onApply }: {
  formId: string; tenantId: string; value: Destination | null; onApply: (d: Destination | null) => void;
}) {
  const [d, setD] = useState<Destination | null>(value);
  return (
    <form id={formId} className="arc-flow-panel-body" onSubmit={(e) => { e.preventDefault(); onApply(d); }}>
      <div className="arc-field">
        <label className="arc-label" htmlFor="flow-exit">Send the caller to</label>
        <DestinationPicker id="flow-exit" tenantId={tenantId} value={d} onChange={setD} allowNone />
        <p className="arc-hint">None ends the call here.</p>
      </div>
    </form>
  );
}
